import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { selectCoordinates } from "features/coordinates";
import { selectMeasureEntities, selectMeasureVisible, selectObjectMeasureResult } from "features/measure/measureSlice";
import { selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { selectPointLines } from "features/pointLine/pointLineSlice";
import { selectClippingPlanes, selectPlainIsolationActive } from "features/render";
import { useObjectTreeHidden } from "hooks/useObjectTreeHidden";
import { selectIsApplyingViewpoint } from "slices/explorer";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

export type PillState = {
    pillPhase: "hidden" | "showing" | "collapsing";
    dotVisible: boolean;
    dotExiting: boolean;
    displayLabel: string | null;
};

export function useModificationPill(isMenuOpen = false): PillState {
    const { t } = useTranslation();
    const activeToolMode = useAppSelector(selectActiveToolMode);
    const isDefaultMode = activeToolMode === ProcoreToolMode.Default;
    const prevModeRef = useRef<ProcoreToolMode>(activeToolMode);

    const { objectTreeHiddenCount: hiddenCount, sheetsShownCount } = useObjectTreeHidden();
    const prevHiddenCountRef = useRef(hiddenCount);
    const prevSheetsShownCountRef = useRef(sheetsShownCount);
    const plainIsolationActive = useAppSelector(selectPlainIsolationActive);
    const prevIsolationActiveRef = useRef(plainIsolationActive);

    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const sectionCount = clippingPlanes.enabled ? clippingPlanes.planes.length : 0;
    const prevSectionCountRef = useRef(sectionCount);

    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const measureEntities = useAppSelector(selectMeasureEntities);
    const measureVisible = useAppSelector(selectMeasureVisible);
    const objectMeasureResult = useAppSelector(selectObjectMeasureResult);
    const rawMeasurementCount =
        coordinates.length +
        lasers.length +
        pointLines.filter((pl) => pl.points.length > 0).length +
        measureEntities.filter((set) => set.length > 0).length +
        // Matches useViewModificationCategories: counts only once the pair has a result.
        (objectMeasureResult ? 1 : 0);
    const measurementCount = measureVisible ? rawMeasurementCount : 0;
    const prevMeasurementCountRef = useRef(measurementCount);

    const isApplyingViewpoint = useAppSelector(selectIsApplyingViewpoint);
    const prevIsApplyingViewpointRef = useRef(isApplyingViewpoint);
    const totalCountBeforeViewpointRef = useRef(0);

    // Counts snapshot taken when entering a tool mode; used to detect all changes across the session.
    // Initialized to zeros when mounted in non-default mode so mode-exit fires correctly even when
    // modifications already existed before this hook mounted.
    const sessionStartCountsRef = useRef<{
        hidden: number;
        section: number;
        measurement: number;
        sheets: number;
    } | null>(activeToolMode !== ProcoreToolMode.Default ? { hidden: 0, section: 0, measurement: 0, sheets: 0 } : null);

    // Isolation works through defaultVisibility = Transparent + a keep-set rather than
    // writing to hidden.ids, so hiddenCount stays 0 while isolation is on. Without this
    // term, totalCount would be 0 and the dot would tear down immediately after the pill
    // collapses. The value 1 is a presence bit — totalCount is only ever asked "are you
    // zero?" and "did you increase?", never displayed or compared to a specific number.
    const totalCount =
        hiddenCount + sheetsShownCount + sectionCount + measurementCount + (plainIsolationActive ? 1 : 0);
    const totalCountRef = useRef(totalCount);
    totalCountRef.current = totalCount;

    // Pill / dot animation state — mirrors prototype's pillPhase + dotVisible + dotExiting
    const [freshLabel, setFreshLabel] = useState<string | null>(null);
    const [pillPhase, setPillPhase] = useState<"hidden" | "showing" | "collapsing">("hidden");
    const [dotVisible, setDotVisible] = useState(false);
    const [dotExiting, setDotExiting] = useState(false);
    const dotVisibleRef = useRef(false);
    useEffect(() => {
        dotVisibleRef.current = dotVisible;
    }, [dotVisible]);

    // Keep the label text stable while collapsing so it doesn't vanish mid-animation
    const displayLabelRef = useRef<string | null>(null);
    if (freshLabel !== null) {
        displayLabelRef.current = freshLabel;
    }
    const displayLabel = displayLabelRef.current;

    const freshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showFreshLabel = useCallback((newLabel: string) => {
        if (freshTimerRef.current) {
            clearTimeout(freshTimerRef.current);
        }
        if (collapseTimerRef.current) {
            clearTimeout(collapseTimerRef.current);
            collapseTimerRef.current = null;
        }
        if (dotTimerRef.current) {
            clearTimeout(dotTimerRef.current);
            dotTimerRef.current = null;
        }
        setDotVisible(false);
        setDotExiting(false);
        setFreshLabel(newLabel);
        freshTimerRef.current = setTimeout(() => setFreshLabel(null), 3000);
    }, []);

    // Drive pill → dot handoff when freshLabel clears
    const prevFreshLabelRef = useRef<string | null>(null);
    useEffect(() => {
        const prev = prevFreshLabelRef.current;
        prevFreshLabelRef.current = freshLabel;

        if (freshLabel !== null) {
            // New label: abort any in-progress collapse, hide dot, show pill
            if (collapseTimerRef.current) {
                clearTimeout(collapseTimerRef.current);
            }
            if (dotTimerRef.current) {
                clearTimeout(dotTimerRef.current);
            }
            setDotVisible(false);
            setDotExiting(false);
            setPillPhase("showing");
        } else if (prev !== null && freshLabel === null) {
            // Timer fired — start collapse
            if (totalCountRef.current > 0) {
                setPillPhase("collapsing");
                // At 300ms the pill has mostly contracted — pop the dot in
                dotTimerRef.current = setTimeout(() => {
                    setDotVisible(true);
                    setDotExiting(false);
                }, 300);
                // At 500ms unmount the pill
                collapseTimerRef.current = setTimeout(() => setPillPhase("hidden"), 500);
            } else {
                setPillPhase("hidden");
            }
        }
    }, [freshLabel]);

    // When all modifications are cleared, tear everything down
    useEffect(() => {
        if (totalCount === 0) {
            if (collapseTimerRef.current) {
                clearTimeout(collapseTimerRef.current);
            }
            if (dotTimerRef.current) {
                clearTimeout(dotTimerRef.current);
            }
            setPillPhase("hidden");
            if (dotVisibleRef.current) {
                setDotExiting(true);
                dotTimerRef.current = setTimeout(() => {
                    setDotVisible(false);
                    setDotExiting(false);
                }, 250);
            }
        }
    }, [totalCount]);

    // Re-fire on mode exit so the pill always shows after leaving an active mode.
    // Snapshots counts on entry so multi-tool sessions (e.g. Measure → PointLine → Section → Default)
    // produce a single accurate label rather than only reflecting the last tool used.
    useEffect(() => {
        const prev = prevModeRef.current;
        prevModeRef.current = activeToolMode;

        if (prev === ProcoreToolMode.Default && activeToolMode !== ProcoreToolMode.Default) {
            sessionStartCountsRef.current = {
                hidden: hiddenCount,
                section: sectionCount,
                measurement: measurementCount,
                sheets: sheetsShownCount,
            };
        } else if (activeToolMode === ProcoreToolMode.Default && prev !== ProcoreToolMode.Default) {
            const start = sessionStartCountsRef.current;
            sessionStartCountsRef.current = null;

            if (start) {
                const hiddenIncreased = hiddenCount > start.hidden;
                const sheetsIncreased = sheetsShownCount > start.sheets;
                const sectionIncreased = sectionCount > start.section;
                const measurementIncreased = measurementCount > start.measurement;
                const changedCount = [hiddenIncreased, sheetsIncreased, sectionIncreased, measurementIncreased].filter(
                    Boolean,
                ).length;

                if (changedCount > 1) {
                    showFreshLabel(t("viewModifications.modificationsApplied"));
                } else if (sectionIncreased) {
                    showFreshLabel(t("viewModifications.sectioningApplied"));
                } else if (measurementIncreased) {
                    showFreshLabel(t("viewModifications.measurementsAdded"));
                } else if (sheetsIncreased) {
                    showFreshLabel(t("viewModifications.sheetsShownApplied"));
                } else if (hiddenIncreased) {
                    showFreshLabel(t("viewModifications.objectsHidden"));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeToolMode]);

    const showDotSilently = useCallback(() => {
        if (!dotVisibleRef.current) {
            setDotVisible(true);
            setDotExiting(false);
        }
    }, []);

    // Snapshot total count when a viewpoint starts applying; show single label when it finishes
    useEffect(() => {
        const prev = prevIsApplyingViewpointRef.current;
        prevIsApplyingViewpointRef.current = isApplyingViewpoint;

        if (isApplyingViewpoint && !prev) {
            totalCountBeforeViewpointRef.current = totalCountRef.current;
        } else if (!isApplyingViewpoint && prev) {
            if (totalCountRef.current > totalCountBeforeViewpointRef.current) {
                if (isMenuOpen) {
                    showDotSilently();
                } else {
                    showFreshLabel(t("viewModifications.modificationsApplied"));
                }
            }
            // Sync prev-count refs so the per-category effect doesn't double-fire
            prevHiddenCountRef.current = hiddenCount;
            prevSheetsShownCountRef.current = sheetsShownCount;
            prevSectionCountRef.current = sectionCount;
            prevMeasurementCountRef.current = measurementCount;
        }
    }, [
        isApplyingViewpoint,
        hiddenCount,
        sheetsShownCount,
        sectionCount,
        measurementCount,
        isMenuOpen,
        showDotSilently,
        showFreshLabel,
        t,
    ]);

    // Show pill label when any modification category increases outside of a viewpoint apply
    useEffect(() => {
        const prevHidden = prevHiddenCountRef.current;
        const prevSheets = prevSheetsShownCountRef.current;
        const prevSection = prevSectionCountRef.current;
        const prevMeasurement = prevMeasurementCountRef.current;
        const prevIsolation = prevIsolationActiveRef.current;

        prevHiddenCountRef.current = hiddenCount;
        prevSheetsShownCountRef.current = sheetsShownCount;
        prevSectionCountRef.current = sectionCount;
        prevMeasurementCountRef.current = measurementCount;
        prevIsolationActiveRef.current = plainIsolationActive;

        if (!isDefaultMode || prevIsApplyingViewpointRef.current) {
            return;
        }

        const hiddenIncreased = hiddenCount > prevHidden || (plainIsolationActive && !prevIsolation);
        const sheetsIncreased = sheetsShownCount > prevSheets;
        const sectionIncreased = sectionCount > prevSection;
        const measurementIncreased = measurementCount > prevMeasurement;

        const changedCount = [hiddenIncreased, sheetsIncreased, sectionIncreased, measurementIncreased].filter(
            Boolean,
        ).length;
        if (changedCount === 0) {
            return;
        }

        if (isMenuOpen) {
            showDotSilently();
            return;
        }

        if (changedCount > 1) {
            showFreshLabel(t("viewModifications.modificationsApplied"));
        } else if (hiddenIncreased) {
            showFreshLabel(t("viewModifications.objectsHidden"));
        } else if (sheetsIncreased) {
            showFreshLabel(t("viewModifications.sheetsShownApplied"));
        } else if (sectionIncreased) {
            showFreshLabel(t("viewModifications.sectioningApplied"));
        } else if (measurementIncreased) {
            showFreshLabel(t("viewModifications.measurementsAdded"));
        }
    }, [
        hiddenCount,
        sheetsShownCount,
        sectionCount,
        measurementCount,
        plainIsolationActive,
        isDefaultMode,
        isMenuOpen,
        showDotSilently,
        showFreshLabel,
        t,
    ]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (freshTimerRef.current) {
                clearTimeout(freshTimerRef.current);
            }
            if (collapseTimerRef.current) {
                clearTimeout(collapseTimerRef.current);
            }
            if (dotTimerRef.current) {
                clearTimeout(dotTimerRef.current);
            }
        };
    }, []);

    return { pillPhase, dotVisible, dotExiting, displayLabel };
}
